import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { downloadRender, useDeleteRenderMutation } from "@/api/renders";
import type { RenderJob, RenderStatus } from "@/types/render";

/** Map internal format names to proper file extensions. */
function formatExtension(format: string): string {
  switch (format) {
    case "prores": return "mov";
    case "h265": return "mp4";
    case "webm": return "webm";
    case "gif": return "gif";
    default: return "mp4";
  }
}

interface RenderHistoryProps {
  jobs: RenderJob[];
}

const STATUS_STYLES: Record<RenderStatus, string> = {
  queued: "bg-warning/10 text-warning border-warning/20",
  rendering: "bg-accent-soft text-accent border-accent/20",
  complete: "bg-success/10 text-success border-success/20",
  error: "bg-danger-soft text-danger border-danger/20",
};

function StatusBadge({ status }: { status: RenderStatus }) {
  return (
    <span
      className={`rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status]}`}
    >
      {status}
    </span>
  );
}

function formatFileSize(bytes: number | null): string {
  if (!bytes) return "--";
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function DownloadCell({ job }: { job: RenderJob }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (job.status !== "complete") return null;

  // mp4-matte downloads arrive as one zip holding fill + matte.
  const isMatteBundle = job.format === "mp4-matte";

  async function handle() {
    setErr(null);
    setBusy(true);
    try {
      const filename = isMatteBundle
        ? `render-${job.id}-fill+matte.zip`
        : `render-${job.id}.${formatExtension(job.format)}`;
      await downloadRender(job.id, filename);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Download failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={handle}
        disabled={busy}
        className="text-xs font-medium text-accent hover:underline disabled:opacity-50"
      >
        {busy ? "Downloading..." : isMatteBundle ? "Download (Fill + Matte)" : "Download"}
      </button>
      {err && <span className="text-[10px] text-danger">{err}</span>}
    </div>
  );
}

function DeleteCell({ job }: { job: RenderJob }) {
  const deleteMutation = useDeleteRenderMutation();
  const [confirming, setConfirming] = useState(false);

  if (confirming) {
    return (
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => {
            deleteMutation.mutate(job.id);
            setConfirming(false);
          }}
          className="text-[10px] text-danger hover:underline"
        >
          Yes
        </button>
        <span className="text-[10px] text-ink-3">/</span>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          className="text-[10px] text-ink-2 hover:underline"
        >
          No
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setConfirming(true)}
      disabled={deleteMutation.isPending}
      className="text-xs text-danger/70 hover:text-danger hover:underline disabled:opacity-50"
    >
      Delete
    </button>
  );
}

export function RenderHistory({ jobs }: RenderHistoryProps) {
  if (jobs.length === 0) {
    return (
      <div className="py-16 text-center text-sm text-ink-3">
        No render jobs yet.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-line">
      <table className="w-full">
        <thead>
          <tr className="border-b border-line bg-surface-2">
            <th className="px-4 py-2 text-left text-xs font-medium text-ink-2">
              Template
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-ink-2">
              Format
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-ink-2">
              Status
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-ink-2">
              Size
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-ink-2">
              Created
            </th>
            <th className="px-4 py-2 text-right text-xs font-medium text-ink-2">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {jobs.map((job) => (
            <tr
              key={job.id}
              className="bg-surface transition-colors hover:bg-surface-3"
            >
              <td className="px-4 py-3">
                <Link
                  to="/renders/$id"
                  params={{ id: job.id }}
                  className="text-sm font-medium text-ink hover:text-accent"
                >
                  {job.template_name ?? job.template_id}
                </Link>
              </td>
              <td className="px-4 py-3 text-xs uppercase text-ink-2">
                {job.format}
              </td>
              <td className="px-4 py-3">
                <StatusBadge status={job.status} />
              </td>
              <td className="px-4 py-3 text-xs text-ink-2">
                {formatFileSize(job.output_size)}
              </td>
              <td className="px-4 py-3 text-xs text-ink-2">
                {new Date(job.created_at).toLocaleString()}
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex items-center justify-end gap-3">
                  <DownloadCell job={job} />
                  <DeleteCell job={job} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
